import { defineStore } from "pinia"
import { apiV1, axiosInstances } from "@/config/axios"
import { useHubsStore } from "@/store/hubs"
import router from "@/router"
import TokenService from "@/services/TokenService"

export const useAuthenticationStore = defineStore("authentication", {
  state: () => ({
    user: {},
    authenticated: false,
    requiresTwoFactor: false,
    challengeId: null,
    sessionVerified: false
  }),
  getters: {
    isLoggedIn: (state) => state.authenticated
  },
  actions: {
    async login(userCredentials) {
      const { data } = await apiV1.post("/login", userCredentials)

      if (data.two_factor_required === true) {
        this.requiresTwoFactor = true
        this.challengeId = data.challenge_id
        return { twoFactor: true }
      }

      this.setUser(data)
      TokenService.setToken(data.token)

      Object.values(axiosInstances).forEach((instance) => {
        instance.setAuthorization(data.token)
      })

      this.requiresTwoFactor = false
      this.challengeId = null
      this.sessionVerified = true

      return { twoFactor: false }
    },
    async logout() {
      await apiV1.post("/users/logout")
      this.unauthorize()
      router.push("/login")
    },
    unauthorize() {
      this.authenticated = false
      this.user = {}
      this.requiresTwoFactor = false
      this.challengeId = null
      this.sessionVerified = false
      useHubsStore().clearState()
      TokenService.removeToken()
      localStorage.removeItem("user_id")
      localStorage.removeItem("hub_id")
      router.push("/login")
    },
    setUser(data) {
      const hubsStore = useHubsStore()
      this.user = data.user
      hubsStore.setHubs(data.user.hubs)
      hubsStore.setActiveHub(data.user.current_hub)
      localStorage.setItem("hub_id", data.user.current_hub.id)
      localStorage.setItem("user_id", data.user.id)
      this.authenticated = true
    },
    async verifySession() {
      if (!this.sessionVerified && TokenService.hasToken()) {
        this.sessionVerified = true
        try {
          const response = await apiV1.get("/users/me")

          if (response.data) {
            this.setUser(response.data)
          }
        } catch {
          this.unauthorize()
        }
      }
    },
    async updateUserDetails(params) {
      const { data } = await apiV1.post("/users/profile", params)
      return data
    },
    async refreshUser() {
      const { data } = await apiV1.get("/users/me")
      if (data) {
        this.setUser(data)
      }
    },
    async sendVerification(type = "email") {
      const { data } = await apiV1.post(
        "/users/request-contact-verification",
        { type }
      )
      return data
    },
    async resetPassword(params) {
      await apiV1.post("/forgotten-password", params)
    },
    async updatePassword(params) {
      await apiV1.post("/reset-password", params)
    },
    async completeTwoFactorChallenge(code, recoveryCode = null) {
      const payload = {
        challenge_id: this.challengeId
      }

      if (recoveryCode) {
        payload.recovery_code = recoveryCode
      } else {
        payload.code = code
      }

      const { data } = await apiV1.post("/two-factor-challenge", payload)

      this.setUser(data)
      TokenService.setToken(data.token)

      Object.values(axiosInstances).forEach((instance) => {
        instance.setAuthorization(data.token)
      })

      this.requiresTwoFactor = false
      this.challengeId = null
      this.sessionVerified = true
    }
  }
})
